import { computeGeometricAdjacency } from './adjacency.js';
import { boundaryLoopOfFaceSet } from './boundary.js';
import { edgeId } from './edges.js';

function compareIds(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}

function contentId(kind, faceIds) {
  return JSON.stringify([kind, ...faceIds]);
}

function rawEdgeIdsOfFace(face) {
  return face.vertexIds.map((vertexId, index) => (
    edgeId(vertexId, face.vertexIds[(index + 1) % face.vertexIds.length])
  ));
}

function faceComponents(regionFaceIds, faceById, explicitEdgeIds) {
  const sortedFaceIds = [...regionFaceIds].sort(compareIds);
  const neighbors = new Map(sortedFaceIds.map((faceId) => [faceId, new Set()]));
  const faceIdsByRawEdgeId = new Map();

  for (const faceId of sortedFaceIds) {
    const face = faceById.get(faceId);
    if (!face) throw new Error(`logical quad discovery references unknown face '${faceId}'`);
    for (const rawEdgeId of rawEdgeIdsOfFace(face)) {
      const incidentFaceIds = faceIdsByRawEdgeId.get(rawEdgeId);
      if (incidentFaceIds) incidentFaceIds.push(faceId);
      else faceIdsByRawEdgeId.set(rawEdgeId, [faceId]);
    }
  }

  for (const [rawEdgeId, incidentFaceIds] of faceIdsByRawEdgeId) {
    const uniqueFaceIds = [...new Set(incidentFaceIds)].sort(compareIds);
    if (uniqueFaceIds.length !== 2 || explicitEdgeIds.has(rawEdgeId)) continue;
    const [a, b] = uniqueFaceIds;
    neighbors.get(a).add(b);
    neighbors.get(b).add(a);
  }

  const visited = new Set();
  const components = [];
  for (const seedFaceId of sortedFaceIds) {
    if (visited.has(seedFaceId)) continue;
    const componentFaceIds = [];
    const queue = [seedFaceId];
    visited.add(seedFaceId);
    while (queue.length > 0) {
      const faceId = queue.shift();
      componentFaceIds.push(faceId);
      for (const neighborFaceId of [...neighbors.get(faceId)].sort(compareIds)) {
        if (visited.has(neighborFaceId)) continue;
        visited.add(neighborFaceId);
        queue.push(neighborFaceId);
      }
    }
    components.push(componentFaceIds.sort(compareIds));
  }
  return components.sort((a, b) => compareIds(contentId('logicalCellComponent', a), contentId('logicalCellComponent', b)));
}

function rotateLoop(loop, startIndex) {
  return [...loop.slice(startIndex), ...loop.slice(0, startIndex)];
}

function quadCellOfComponent({
  faceIds,
  regionId,
  faceById,
  cornerIdByVertexId,
  explicitEdgeIds,
  diagonalTopologyEdgeIds,
}) {
  if (faceIds.length !== 2) return null;
  const faces = faceIds.map((faceId) => faceById.get(faceId));
  if (faces.some((face) => !face || face.vertexIds.length !== 3)) return null;

  const firstEdges = new Set(rawEdgeIdsOfFace(faces[0]));
  const sharedRawEdgeIds = rawEdgeIdsOfFace(faces[1])
    .filter((rawEdgeId) => firstEdges.has(rawEdgeId))
    .sort(compareIds);
  if (sharedRawEdgeIds.length !== 1) return null;
  const [diagonalTopologyEdgeId] = sharedRawEdgeIds;
  if (explicitEdgeIds.has(diagonalTopologyEdgeId)) return null;
  if (!diagonalTopologyEdgeIds.has(diagonalTopologyEdgeId)) return null;

  let boundaryVertexIds;
  try {
    boundaryVertexIds = boundaryLoopOfFaceSet(faces, 'discoverLogicalQuadCells');
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('discoverLogicalQuadCells region ')) return null;
    throw error;
  }
  if (boundaryVertexIds.length !== 4 || new Set(boundaryVertexIds).size !== 4) return null;

  const woundCornerIds = boundaryVertexIds.map((vertexId) => cornerIdByVertexId.get(vertexId));
  if (woundCornerIds.some((cornerId) => cornerId === undefined) || new Set(woundCornerIds).size !== 4) return null;

  const canonicalStart = woundCornerIds.reduce(
    (bestIndex, cornerId, index) => (compareIds(cornerId, woundCornerIds[bestIndex]) < 0 ? index : bestIndex),
    0,
  );
  const cornerIds = rotateLoop(woundCornerIds, canonicalStart);
  const canonicalBoundaryVertexIds = rotateLoop(boundaryVertexIds, canonicalStart);
  const boundaryTopologyEdgeIds = canonicalBoundaryVertexIds.map((vertexId, index) => (
    edgeId(vertexId, canonicalBoundaryVertexIds[(index + 1) % canonicalBoundaryVertexIds.length])
  ));
  if (new Set(boundaryTopologyEdgeIds).size !== 4) return null;

  return {
    id: contentId('logicalQuad', faceIds),
    regionId,
    faceIds,
    diagonalTopologyEdgeId,
    cornerIds,
    boundaryTopologyEdgeIds,
  };
}

/**
 * Discover barrier-separated logical cells and identify the components that
 * are valid two-triangle quads. Pure/read-only; callers may reuse an already
 * computed geometric adjacency snapshot for the same topology.
 */
export function discoverLogicalQuadCells(topology, adjacency = computeGeometricAdjacency(topology)) {
  const faceById = new Map(topology.faces.map((face) => [face.id, face]));
  const explicitEdgeIds = new Set(topology.explicitEdgeIds ?? []);
  const diagonalTopologyEdgeIds = new Set(adjacency.diagonalTopologyEdgeIds);
  const cornerIdByVertexId = new Map();
  for (const corner of adjacency.corners) {
    for (const vertexId of corner.vertexIds) cornerIdByVertexId.set(vertexId, corner.id);
  }

  const cells = [];
  const nonQuadComponents = [];
  for (const region of [...adjacency.regions].sort((a, b) => compareIds(a.id, b.id))) {
    const components = faceComponents(region.faceIds, faceById, explicitEdgeIds);
    for (const faceIds of components) {
      const cell = quadCellOfComponent({
        faceIds,
        regionId: region.id,
        faceById,
        cornerIdByVertexId,
        explicitEdgeIds,
        diagonalTopologyEdgeIds,
      });
      if (cell) cells.push(cell);
      else {
        nonQuadComponents.push({
          id: contentId('logicalCellComponent', faceIds),
          regionId: region.id,
          faceIds,
        });
      }
    }
  }

  cells.sort((a, b) => compareIds(a.id, b.id));
  nonQuadComponents.sort((a, b) => compareIds(a.id, b.id));
  return { cells, nonQuadComponents };
}
